import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import { Avatar, AvatarGroup } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import { DataValueMap } from 'Helpers/Testing';
import useMobileLayout from 'Hooks/useMobileLayout';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import { FDBox, FDIcon, FDLabelText } from '@facet-wealth/design';

const useStyles = buildUseStyles('ThreadParticipantGroup', (theme) => ({
  root: {
    width: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    borderRadius: theme.radius.md,
  },
  small: {},
  medium: {
    height: 65,
    width: 65,
  },
  large: {
    height: 90,
    width: 90,
  },
}));

const ThreadParticipantGroup = ({
  className,
  max,
  threadParticipants,
  size,
  spacing,
  onClick,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const renderedParticipants = threadParticipants.slice(0, max);
  const hasMoreParticipants = threadParticipants.length > max;
  const postTextMobile = hasMoreParticipants ? threadParticipants.length - max : '';
  const postTextDesktop = postTextMobile ? `${postTextMobile} More` : '';
  const postText = isMobile ? postTextMobile : postTextDesktop;

  return (
    <ListItem
      button={!!onClick}
      className={classNames(className, classes.root)}
      onClick={(e) => onClick(e)}
    >
      <AvatarGroup
        spacing={spacing}
        {...DataValueMap.init('thread-participant-group').build()}
      >
        {renderedParticipants.map((avatar, i) => {
          return (
            <Avatar
              className={classes[size]}
              key={avatar.id()}
              alt={avatar.attr('name')}
              src={avatar.relationship(fr.relationships.profileImage.name)?.link('related')?.url()}
              {...DataValueMap.init('thread-participant-avatar')
                .append(DataValueMap.keyMap.dataResourceId, avatar.id())
                .append(DataValueMap.keyMap.dataIndex, i.toString())
                .build()
            }
            />
          );
        })}
      </AvatarGroup>
      {hasMoreParticipants && (
        // The FDBox flexShrink = 0 is to make sure the line does not wrap. Text components don't have whitespace: nowrap currently
        <FDBox flexShrink={0}>
          <FDLabelText value={postText} size={'lg'} />
        </FDBox>
      )}
      {!!onClick && <FDIcon icon={'arrowDropDown'} color={hasMoreParticipants ? 'textPrimary' : 'textSecondary'} />}
    </ListItem>
  );
};

ThreadParticipantGroup.defaultProps = {
  className: '',
  max: 3,
  size: 'sm',
  spacing: 'medium',
  onClick: undefined,
};

ThreadParticipantGroup.propTypes = {
  className: PropTypes.string,
  max: PropTypes.number,
  threadParticipants: PropTypes.arrayOf(fr.Resource).isRequired,
  size: PropTypes.string,
  spacing: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
};

export { ThreadParticipantGroup };
